import type { CompletedMarking } from './attendanceJourneyTimeline.interface';
import type IWorkAddress from './workAddress.interface';

/**
 * Attendance event contracts consumed by the PWA (marking portal / kiosk).
 *
 * Mirrors the `attendance.newEvent` API operation payload. The server owns
 * `id`, `createdAt` and `updatedAt`; the client only sends the fields below. All time values
 * travel as epoch milliseconds because the callable transport cannot carry Firestore
 * `Timestamp` instances — the server converts them.
 */

export const ATTENDANCE_TYPE = {
  SHIFT_START: 'SHIFT_START',
  SHIFT_END: 'SHIFT_END',
  BREAK_START: 'BREAK_START',
  BREAK_END: 'BREAK_END',
  SHIFT_CHANGES: 'SHIFT_CHANGES',
} as const;

export type AttendanceType = (typeof ATTENDANCE_TYPE)[keyof typeof ATTENDANCE_TYPE];

/** Marks that advance the employee journey FSM (start / break / end). */
export const JOURNEY_ATTENDANCE_TYPES: readonly AttendanceType[] = [
  ATTENDANCE_TYPE.SHIFT_START,
  ATTENDANCE_TYPE.SHIFT_END,
  ATTENDANCE_TYPE.BREAK_START,
  ATTENDANCE_TYPE.BREAK_END,
];

export const ATTENDANCE_ACTOR_ROLE = {
  EMPLOYEE: 'EMPLOYEE',
  SUPERVISOR: 'SUPERVISOR',
  SYSTEM: 'SYSTEM',
} as const;

export type AttendanceActorRole =
  (typeof ATTENDANCE_ACTOR_ROLE)[keyof typeof ATTENDANCE_ACTOR_ROLE];

export const SHIFT_CHANGE_TYPE = {
  TIME_ADJUSTMENT: 'time_adjustment',
  LEAVE: 'leave',
} as const;

export type ShiftChangeType = (typeof SHIFT_CHANGE_TYPE)[keyof typeof SHIFT_CHANGE_TYPE];

/** Device metadata attached to a marking, when available. */
export interface AttendanceDeviceInfo {
  platform?: string;
  userAgent?: string;
  appVersion?: string;
  model?: string;
  osVersion?: string;
}

/** SHIFT_CHANGES payload. Times are epoch milliseconds. */
export interface CreateShiftChangesParams {
  solicitationBy: AttendanceActorRole;
  changeType: ShiftChangeType;
  actualStartAt?: number;
  actualEndAt?: number;
  actualBreakStartAt?: number;
  actualBreakEndAt?: number;
  leaveType?: string;
  isExcused?: boolean;
  previousLeaveType?: string;
}

/** Payload for creating an attendance event (server sets id, createdAt, updatedAt). */
export interface CreateAttendanceEventParams {
  organizationId: string;
  attendanceRecordId: string;
  employeeId: string;
  attendanceType: AttendanceType;
  actorId: string;
  actorRole: AttendanceActorRole;
  timeZone: string;
  shiftChanges?: CreateShiftChangesParams;
  reminderFromAttendanceType?: AttendanceType;
  address?: IWorkAddress;
  device?: AttendanceDeviceInfo;
  actorFullname?: string;
  /** Device clock at marking time, epoch milliseconds. */
  createdAtDevice?: number;
}

export interface CreateAttendanceEventResponse {
  id: string;
  message?: string;
}

/** Params for the cancel attendance event callable. */
export interface CancelAttendanceEventParams {
  organizationId: string;
  attendanceEventId: string;
  [key: string]: unknown;
}

/** Raw body returned by the cancel-by-token HTTP endpoint. */
export interface CancelAttendanceEventResponseBody {
  error?: string;
  [key: string]: unknown;
}

/** Normalized result of the cancel-by-token HTTP endpoint. */
export interface CancelAttendanceEventHttpResult {
  ok: boolean;
  status: number;
  data: CancelAttendanceEventResponseBody;
}

/** GPS payload when the employee marked from a device with location. */
export interface AttendanceEventMarkPosition {
  lat: number;
  lon: number;
  accuracy?: number | null;
  timestamp?: number | null;
}

/** Stored attendance event document (Firestore `attendance_events`). */
export interface AttendanceEventSummary {
  id: string;
  organizationId: string;
  attendanceRecordId: string;
  employeeId: string;
  attendanceType: AttendanceType;
  actorId?: string;
  actorRole?: AttendanceActorRole;
  timeZone?: string;
  /** createdAt as epoch milliseconds when present. */
  createdAtMs: number | null;
  /**
   * Device GPS at marking time (`position.location` on the Firestore doc).
   * Absent when the mark had no geolocation (kiosk / supervisor / remote without GPS).
   */
  markPosition?: AttendanceEventMarkPosition | null;
}

/** Journey FSM snapshot derived from stored attendance events for a record. */
export interface JourneyMarkState {
  hasShiftStart: boolean;
  isOnBreak: boolean;
  hasBreakEnd: boolean;
  hasShiftEnd: boolean;
  types: AttendanceType[];
  /** Sequential completed markings (stops at first gap), with event timestamps when available. */
  completedMarkings: CompletedMarking[];
}

export interface ListAttendanceEventsByRecordParams {
  organizationId: string;
  attendanceRecordId: string;
}
